import {
  type Fingering,
  findFingerings,
  findGuitarChord,
} from "chord-fingering";
import { Chord } from "tonal";
import { normalizeChordSymbol } from "./format";
import type { ChordDefinition } from "./types";

// Register custom chord types (side effect import)
import "./customChordTypes";

/**
 * Number of strings on a standard guitar.
 */
const GUITAR_STRINGS = 6;

/**
 * Parse a position string from chord-fingering into a frets array.
 * Position strings can be:
 * - "x32010" - compact format with single digits
 * - "8-10-10-9-8-8" - hyphenated format for positions >= 10
 * - "x-x-10-9-8-0" - hyphenated format with muted strings
 */
function parsePositionString(positionString: string): (number | "x")[] {
  // Check if it's hyphenated format (contains "-" that separates positions)
  // Note: "x" by itself doesn't count as hyphenated
  const hasHyphen =
    positionString.includes("-") && !/^x+$/.test(positionString);

  if (hasHyphen) {
    // Hyphenated format: "8-10-10-9-8-8" or "x-x-10-9-8-0"
    return positionString.split("-").map((s) => (s === "x" ? "x" : Number(s)));
  }

  // Compact format: "x32010"
  return positionString.split("").map((c) => (c === "x" ? "x" : Number(c)));
}

/**
 * Convert chord-fingering barre to our barres format.
 * Returns the fret number of the barre, or undefined if no barre.
 */
function convertBarre(
  barre: { fret: number; stringIndices: number[] } | null,
): number[] | undefined {
  if (!barre) return undefined;
  return [barre.fret];
}

/**
 * Calculate baseFret for display purposes.
 * For positions that fit within the first 4 frets, baseFret is 1.
 * For higher positions, baseFret is the minimum non-zero fret.
 */
function calculateBaseFret(frets: (number | "x")[]): number {
  const numericFrets = frets.filter((f): f is number => typeof f === "number");
  const nonZeroFrets = numericFrets.filter((f) => f > 0);

  if (nonZeroFrets.length === 0) return 1;

  const minFret = Math.min(...nonZeroFrets);
  const maxFret = Math.max(...nonZeroFrets);

  // If all frets fit in first 4 positions, use baseFret 1
  if (maxFret <= 4) return 1;

  // Otherwise use the minimum fret as the base
  return minFret;
}

/**
 * Capitalize the root note for chord-fingering compatibility.
 * Preserves the rest of the chord symbol (e.g., "am7" -> "Am7", "c#m" -> "C#m").
 */
function capitalizeRoot(symbol: string): string {
  if (symbol.length === 0) return symbol;
  return symbol[0]!.toUpperCase() + symbol.slice(1);
}

/**
 * Normalize chord symbol aliases that chord-fingering doesn't recognize.
 * Converts common alternative notations to forms chord-fingering accepts.
 */
function normalizeForChordFingering(symbol: string): string {
  // chord-fingering doesn't recognize "minor" spelled out, only "m"
  return symbol.replace(/minor/gi, "m");
}

/**
 * Convert a Fingering to our ChordDefinition format.
 */
function fingeringToChordDef(
  fingering: Fingering,
  displayName: string,
): ChordDefinition | undefined {
  const frets = parsePositionString(fingering.positionString);

  if (frets.length !== GUITAR_STRINGS) {
    return undefined;
  }

  return {
    name: displayName,
    baseFret: calculateBaseFret(frets),
    frets,
    barres: convertBarre(fingering.barre),
  };
}

/**
 * Fallback: use tonal.js to parse the chord and generate fingerings from notes.
 * This handles chords that tonal.js knows but chord-fingering doesn't recognize by symbol.
 */
function findChordByNotes(symbol: string): Fingering | undefined {
  const tonalChord = Chord.get(symbol);
  if (tonalChord.empty || tonalChord.notes.length === 0) {
    return undefined;
  }

  // Get notes and bass for findFingerings
  const notes = tonalChord.notes;
  const bass = tonalChord.tonic ?? notes[0];

  // findFingerings returns all possible fingerings
  const fingerings = findFingerings(notes, [], bass);
  if (!fingerings || fingerings.length === 0) {
    return undefined;
  }

  // Return the first (easiest) fingering
  return fingerings[0];
}

/**
 * Look up a chord using algorithmic fingering generation.
 * Returns the easiest (lowest difficulty) voicing, or undefined if not found.
 *
 * Strategy:
 * 1. Normalize the symbol and try findGuitarChord (fast path)
 * 2. If that fails, use tonal.js to parse and findFingerings (fallback)
 */
export function getBuiltInChord(name: string): ChordDefinition | undefined {
  // Normalize unicode sharps/flats and trim whitespace
  const normalized = normalizeChordSymbol(name);

  // Capitalize root and normalize aliases for chord-fingering
  const forLookup = normalizeForChordFingering(capitalizeRoot(normalized));

  // Try chord-fingering's symbol-based lookup first
  const result = findGuitarChord(forLookup);
  const firstFingering = result?.fingerings[0];
  if (firstFingering) {
    return fingeringToChordDef(firstFingering, normalized);
  }

  // Fallback: parse with tonal.js and use note-based fingering lookup
  const fallbackFingering = findChordByNotes(forLookup);
  if (fallbackFingering) {
    return fingeringToChordDef(fallbackFingering, normalized);
  }

  return undefined;
}
